import { GetServerSidePropsContext } from "next";

import getCitiesFaq from "api/requests/directus/getCitiesFaqs";
import getGeolocation from "api/requests/getGeolocation";
import { getUserLocationData } from "api/requests/getUserLocation";
import locations from "common/location.data";
import states from "common/states.data";
import getStaticFinderConfig from "common/staticFinderConfig/getStaticFinderConfig";
import { DirectusFAQs } from "custom-types/Directus";
import { CountryCode, DispensaryTypes } from "custom-types/Finder";
import {
  InitialConfigContext,
  InitialContext,
  InitialLocationContext,
} from "custom-types/FinderPage";
import { Location } from "custom-types/Location";
import { RetailType } from "custom-types/Store";
import logger from "lib/logger";
import { radiusToZoom } from "utils/finder/mapUtils";
import { getUserLocationInfoFromCookie } from "utils/getUserLocationFromCookie";
import { getLocationSlug, normalizeFinderLocation } from "utils/locationUtils";
import { getCountryCodeHeader } from "utils/requestContext";
import { validateZip } from "utils/zipcodeValidation";

export default async function getFinderContextData(
  ctx: GetServerSidePropsContext
): Promise<InitialContext> {
  const { req, query, resolvedUrl } = ctx;

  const retailType = getRetailTypeFromUrl(resolvedUrl);

  const countryCode = getCountryCodeHeader(ctx) as CountryCode;

  const userLocationCookie = getUserLocationInfoFromCookie(req.headers.cookie);
  const cookieLocation =
    userLocationCookie && normalizeFinderLocation(userLocationCookie);

  const state = query?.location && query?.location[0];
  const city = query?.location && query?.location[1];

  let isLocationPage = !!state;

  const locationParam = getLocationSlug({ city, state });

  let finderLocation: Location | undefined;
  let FAQData: { data: DirectusFAQs } = { data: {} };
  if (retailType === "dispensary") {
    const { draft = false, filter = [], location } = query;
    const locationPath = location
      ? `/${Array.isArray(location) ? location.join("/") : location}`
      : "";
    FAQData = await getCitiesFaq(locationPath, !!draft, filter, countryCode);
  }

  if (locationParam) {
    const foundState = states.find((st) => st.slug === state);
    if (
      city &&
      Object.keys(locations).find((key) => key.toString().includes(city))
    ) {
      finderLocation = normalizeFinderLocation(
        locations[`${city}-${foundState?.short}` as keyof typeof locations]
      );
    } else if (
      !city &&
      state &&
      Object.keys(locations).find((key) => {
        return key.toString().includes(state);
      })
    ) {
      finderLocation = normalizeFinderLocation(
        locations[state as keyof typeof locations]
      );
    }
    if (!finderLocation?.coordinates) {
      try {
        finderLocation = normalizeFinderLocation(
          await getGeolocation({ address: locationParam })
        );
      } catch (error) {
        logger.error(
          `Error fetching location by address with locationParam ${locationParam}.`,
          error
        );
        finderLocation = await getUserLocation(countryCode, ctx);
        isLocationPage = false;
      }
    }
  } else if (cookieLocation) {
    finderLocation = cookieLocation;
  } else {
    finderLocation = await getUserLocation(countryCode, ctx);
  }

  const staticConfig = getStaticFinderConfig(countryCode, retailType);
  const configContext: InitialConfigContext = {
    FAQData,
    isLocationPage,
    retailType,
    ...staticConfig,
  };

  // Get user location data. This is not for finder location
  const userLocation = cookieLocation || finderLocation;

  const { zip, lat, lng, lon, zoom } = query;

  if (zip && finderLocation) {
    finderLocation.zip =
      typeof zip === "string" && validateZip(zip) ? zip : undefined;
  } else if ((query.state || query.city) && finderLocation) {
    finderLocation.zip = undefined;
  }

  const locationSlug = finderLocation?.geoSlug;
  const normalizedLocationSlug =
    finderLocation && !finderLocation?.city
      ? locationSlug?.slice(0, finderLocation?.geoSlug?.lastIndexOf("-us"))
      : locationParam;

  const radius = locations[normalizedLocationSlug as keyof typeof locations]
    ?.radius
    ? `${locations[normalizedLocationSlug as keyof typeof locations]?.radius}mi`
    : null;

  let processedZoom = 13;

  if (radius) {
    processedZoom = radiusToZoom(
      parseInt(radius.slice(0, radius.lastIndexOf("mi")))
    );
  } else if (zoom) {
    processedZoom = parseInt(`${zoom}`);
  }

  // We want to use lon instead of lng for consistency across the app, but will support both here for legacy urls
  const mapContext = {
    center: {
      lat: lat && (lon || lng) ? Number(lat) : finderLocation?.coordinates?.lat,
      lon:
        lat && (lon || lng)
          ? Number(lon || lng)
          : finderLocation?.coordinates?.lon,
    },
    zoom: typeof processedZoom === "number" ? processedZoom : 13,
  };

  const locationContext: InitialLocationContext = {
    finderLocation,
    locationParam,
    userLocation,
  };

  const contextData: InitialContext = {
    configContext,
    locationContext,
    mapContext,
  };

  return contextData;
}

const getUserLocation = async (
  countryCode: string,
  ctx: GetServerSidePropsContext
) => normalizeFinderLocation(await getUserLocationData(countryCode, ctx));

export const getRetailTypeFromUrl = (pathname: string): RetailType => {
  const finderRoutes = Object.keys(DispensaryTypes);
  let retailType: RetailType = DispensaryTypes.dispensaries;
  finderRoutes.forEach((route) => {
    if (pathname.includes(route)) {
      retailType = DispensaryTypes[route as keyof typeof DispensaryTypes];
      return;
    }
  });

  return retailType;
};
